import React from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { useBusinessDate } from 'reusable/useBusinessDate';
import user_logo from 'assets/icons/user.svg';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { DeviceTableProps } from './DeviceField';
import { useTranslation } from 'react-i18next';
import TableScroll, { PAGE_TITLE_ID } from 'reusable/TableScroll';

const DeviceTable: React.FC<DeviceTableProps> = ({
  devices,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation('device');
  const today = useBusinessDate();
  const getColorClass = (status: string) => {
    let stylesSnippet = 'flex items-center justify-center rounded w-24 h-7  ';
    // EV-412（axe の color-contrast）。以前は success / primary/50 2.11、warning / #FFF8E7 1.65、
    // error / #FFE5EE 4.31。文字の色だけ濃くした（6.12・6.47・5.45）。success などの token はアイコンでも使うので変えない。
    if (status === 'available') {
      return stylesSnippet + 'bg-primary/50 text-green-800';
    } else if (status === 'in_repair') {
      return stylesSnippet + 'bg-[#FFF8E7] text-yellow-800';
    } else if (status === 'damaged') {
      return stylesSnippet + 'bg-[#FFE5EE] text-red-700';
    }
  };

  // RV-009 / ADM-05。割り当て日・購入日・保証期限は日付だけ（YYYY-MM-DD）。
  // new Date() は UTC の0時として読むため、日本より西の時間帯では1日前になっていた。
  // parseISO は日付だけの値を端末の0時として読むので、日付が変わらない。
  // 型は Date だが、API の値は文字列で届く。
  const checkDate = (initialDate: string | Date | null | undefined) => {
    if (!initialDate) return '-';
    const date = initialDate instanceof Date ? initialDate : parseISO(initialDate);
    return isValid(date) ? format(date, t('table.date-format')) : '-';
  };

  // EV-116。保証期限当日は有効で、翌日0:00 JSTから期限切れ（2026-10-05 PM回答）。
  // 日付だけの値をUTCの時刻にせず、日本時間の今日と暦日として比べる。
  const isWarrantyExpired = (value: string | Date | null | undefined) => {
    if (!value) return false;
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) && format(date, 'yyyy-MM-dd') < today;
  };

  return (
    <main className="flex flex-col my-6">
      {/* EV-309。768px などの狭い幅では、表に最小の幅を持たせて表の外枠の中だけを横に送る（EV-301 と同じ TableScroll）。
          データが無いときの文は外枠の外に置き、見える幅の中央に出す（PR #244 の Codex の指摘と同じ）。 */}
      <TableScroll labelledBy={PAGE_TITLE_ID}>
      <div className="grid grid-cols-9 gap-4 px-2 py-2.5 bg-primary/50 rounded-md w-full">
        <div className="col-span-1 ">{t('table.device-id')}</div>
        <div className="col-span-2 ">{t('table.device-name')}</div>
        <div className="col-span-2 ">{t('table.status')}</div>
        <div className="col-span-1  text-center">
          {t('table.assigned-date')}
        </div>
        <div className="col-span-1  text-center">{t('table.purchase')}</div>
        <div className="col-span-1  text-center"> {t('table.warranty')}</div>
        <div className="col-span-1  text-center"> {t('table.action')}</div>
      </div>
      {devices.length === 0 ? null : (
        devices.map((device, index) => (
          <div
            key={index}
            className="grid grid-cols-9 gap-3 px-2 sm:h-8 md:h-12 lg:h-16 border-b"
          >
            <div className="col-span-1 flex items-center  text-neutral/100">
              {device.device_id}
            </div>
            <div className="col-span-2 flex items-center  truncate">
              {device.name}
            </div>
            {device.status === 'using' ? (
              <div className="col-span-2 flex space-x-2 items-center truncate">
                {
                  <img
                    src={device.user_info?.profile_picture || user_logo}
                    alt=""
                    className="h-10 w-10 object-cover rounded-full"
                  />
                }
                {/* EV-315。表の欄の中の空は「-」（EV-311。以前は「データなし」・空白）。 */}
                <div>
                  <div className="text-caption">
                    {device.user_info?.name || '-'}
                  </div>
                  <div className=" text-neutral/100 text-overline w-32">
                    {device.user_info?.phone || device.user_info?.email || '-'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-span-2 flex items-center">
                <div className={getColorClass(device.status)}>
                  {t(`status.${device.status}`)}
                </div>
              </div>
            )}
            <div className="col-span-1 flex items-center justify-center  text-center">
              {/* 割り当て日。更新日時を出していたため、割り当ての無い機器にも日付が出ていた。 */}
              {checkDate(device.assigned_date)}
            </div>
            <div className="col-span-1 flex items-center justify-center  text-center">
              {checkDate(device.purchase_date)}
            </div>
            <div
              className={`col-span-1 flex items-center justify-center  text-center ${
                isWarrantyExpired(device.warranty_date)
                  ? 'text-error'
                  : 'text-neutral/300'
              }`}
            >
              {checkDate(device.warranty_date)}
            </div>
            <div className="col-span-1 flex items-center justify-center gap-3 text-neutral/100 cursor-pointer">
              <button
                type="button"
                className="inline-flex items-center"
                aria-label={t('ui.edit', { ns: 'common' })}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(device.id);
                }}
              >
                <IconEdit
                  size={20}
                  stroke={1.5}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="inline-flex items-center"
                aria-label={t('ui.delete', { ns: 'common' })}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(device.id);
                }}
              >
                <IconTrash
                  size={20}
                  stroke={1.5}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        ))
      )}
      </TableScroll>
      {devices.length === 0 && <div className="text-center my-4">{t('table.no-data')}</div>}
    </main>
  );
};
export default DeviceTable;
